import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
} from "@mui/material";
import {
  SwapVert as SwapVertIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { SORT_OPTIONS, SORT_LABELS } from "../../utils/filterUtils";

const SortDropdown = ({
  activeSort,
  onSortChange,
  type, // "new_arrivals" or "on_sale"
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const themeColor = "#FF6B00";
  const themeGradient = "linear-gradient(135deg, #FF8C33 0%, #FF6B00 50%, #E66000 100%)";
  const themeBgLight = "rgba(255, 107, 0, 0.08)";

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const handleSelect = (option) => {
    onSortChange(option);
    setOpen(false);
  };

  const isNonDefault = activeSort !== SORT_OPTIONS.MOST_RELEVANT;

  return (
    <Box sx={{ position: "relative" }}>
      {/* Sort Button */}
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          cursor: "pointer",
          px: 2,
          py: 1,
          borderRadius: "12px",
          border: `1.5px solid ${isNonDefault ? themeColor : "#E0E0E0"}`,
          background: isNonDefault ? `${themeColor}08` : "#fff",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          userSelect: "none",
          "&:hover": {
            borderColor: themeColor,
            background: themeBgLight,
            transform: "translateY(-1px)",
            boxShadow: `0 4px 12px ${themeColor}15`,
          },
          "&:active": {
            transform: "translateY(0px)",
          },
        }}
      >
        <SwapVertIcon
          sx={{
            fontSize: 18,
            color: isNonDefault ? themeColor : "#888",
            transition: "color 0.2s ease",
          }}
        />
        <Typography
          sx={{
            fontFamily: "Poppins-Medium",
            fontSize: "13px",
            color: isNonDefault ? "#333" : "#666",
            whiteSpace: "nowrap",
            transition: "color 0.2s ease",
          }}
        >
          {SORT_LABELS[activeSort] || "Sort"}
        </Typography>
        <ArrowDownIcon
          sx={{
            fontSize: 18,
            color: "#888",
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Dropdown Popper */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: "right top",
            }}
            timeout={250}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 1,
                borderRadius: "14px",
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.04)",
                overflow: "hidden",
                minWidth: "220px",
                background: "#fff",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  sx={{
                    py: 0.5,
                  }}
                >
                  {Object.entries(SORT_LABELS).map(([key, label]) => {
                    const isActive = activeSort === key;
                    return (
                      <MenuItem
                        key={key}
                        onClick={() => handleSelect(key)}
                        sx={{
                          px: 2.5,
                          py: 1.3,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          transition: "all 0.15s ease",
                          background: isActive ? themeBgLight : "transparent",
                          "&:hover": {
                            background: isActive
                              ? `${themeColor}15`
                              : "rgba(0,0,0,0.03)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: isActive
                              ? "Poppins-SemiBold"
                              : "Poppins-Regular",
                            fontSize: "13px",
                            color: isActive ? themeColor : "#555",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {label}
                        </Typography>
                        {isActive && (
                          <CheckIcon
                            sx={{
                              fontSize: 16,
                              color: themeColor,
                            }}
                          />
                        )}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default SortDropdown;
